import { cn } from 'corso-admin-ui';
import { useRef } from 'react';
import { match } from 'ts-pattern';
import { useLocalStorage, useWindowSize } from 'usehooks-ts';
import { sidekickCopy } from './sidekickCopy';

const DEFAULT_WIDTH = 356;
const MIN_WIDTH = 300;
const MAX_VIEWPORT_FRACTION = 1 / 3;
const KEYBOARD_STEP = 16;

const maxWidthFor = (viewportWidth: number) =>
  Math.max(MIN_WIDTH, viewportWidth * MAX_VIEWPORT_FRACTION);

const clampWidth = (width: number, maxWidth = maxWidthFor(window.innerWidth)) =>
  Math.round(Math.min(maxWidth, Math.max(MIN_WIDTH, width)));

/** The grid track for the open Sidekick; CSS caps it as the window narrows, keeping the stored width for when it widens again. */
export const sidekickColumn = (width: number) =>
  `minmax(0px, min(${width}px, ${MAX_VIEWPORT_FRACTION * 100}vw))`;

export function useSidekickWidth() {
  const [width, setWidth] = useLocalStorage('sidekick-width', DEFAULT_WIDTH);
  return [
    Number.isFinite(width) ? Math.max(MIN_WIDTH, width) : DEFAULT_WIDTH,
    (next: number) => setWidth(clampWidth(next)),
  ] as const;
}

/**
 * Sits on the left edge of the Sidekick column; dragging left widens it.
 * While dragging only `onPreview` fires, so nothing re-renders or persists until release.
 */
export function SidekickResizeHandle({
  width,
  onPreview,
  onCommit,
  className,
}: {
  width: number;
  onPreview: (width: number) => void;
  onCommit: (width: number) => void;
  className?: string;
}) {
  const maxWidth = maxWidthFor(useWindowSize().width);
  const shownWidth = clampWidth(width, maxWidth);
  const drag = useRef<{
    startX: number;
    startWidth: number;
    width: number | null;
  } | null>(null);

  const endDrag = () => {
    if (!drag.current) return;
    if (drag.current.width !== null) onCommit(drag.current.width);
    drag.current = null;
    document.documentElement.classList.remove(
      'cursor-col-resize',
      'select-none',
    );
  };

  return (
    <div
      // a focusable window splitter, which an `hr` can't be
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
      role="separator"
      aria-orientation="vertical"
      aria-label={sidekickCopy.header.resize}
      aria-valuenow={shownWidth}
      aria-valuemin={MIN_WIDTH}
      aria-valuemax={Math.round(maxWidth)}
      tabIndex={0}
      className={cn(
        'group/resize z-10 -ml-1 flex w-3 cursor-col-resize touch-none justify-center focus-visible:outline-none',
        className,
      )}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        // start from the rendered width, which a narrow window can squeeze below the stored one
        const grid = event.currentTarget.parentElement;
        const columns = grid ? getComputedStyle(grid).gridTemplateColumns : '';
        drag.current = {
          startX: event.clientX,
          startWidth:
            Number.parseFloat(columns.split(' ').at(-1) ?? '') || shownWidth,
          width: null,
        };
        document.documentElement.classList.add(
          'cursor-col-resize',
          'select-none',
        );
      }}
      onPointerMove={(event) => {
        if (!drag.current) return;
        drag.current.width = clampWidth(
          drag.current.startWidth + drag.current.startX - event.clientX,
        );
        onPreview(drag.current.width);
      }}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onLostPointerCapture={endDrag}
      onDoubleClick={() => onCommit(DEFAULT_WIDTH)}
      onKeyDown={(event) => {
        const next = match(event.key)
          .with('ArrowLeft', () => shownWidth + KEYBOARD_STEP)
          .with('ArrowRight', () => shownWidth - KEYBOARD_STEP)
          .with('Home', () => MIN_WIDTH)
          .with('End', () => maxWidth)
          .otherwise(() => null);
        if (next === null) return;
        event.preventDefault();
        onCommit(clampWidth(next));
      }}
    >
      <div className="my-auto h-10 w-1 rounded-full bg-neutral-600 transition-colors group-hover/resize:bg-neutral-400 group-focus-visible/resize:bg-neutral-300 group-active/resize:bg-neutral-300" />
    </div>
  );
}
